//#region Imports
import React, { useState, useEffect, useRef } from "react";
import {
  StyleSheet,
  Platform,
  SafeAreaView,
  ScrollView,
  FlatList,
  View,
} from "react-native";
import { useTheme } from "@react-navigation/native";
import { useAccountContext } from "../../hooks/useAccountContext";
import CustomButton from "../../components/CustomButton";
import NewLeaveScreen from "../SelfService/MyLeaves/NewLeaveScreen";
import MyLeaves from "../SelfService/MyLeaves/MyLeaves";
import PersonalLeaveBalancesScreen from "../SelfService/MyLeaves/PersonalLeaveBalancesScreen";
import TeamsLeaveBalancesScreen from "../SelfService/Team/TeamsLeaveBalancesScreen";
import TeamsLeaveSchedulerScreen from "../SelfService/Team/TeamsLeaveSchedulerScreen";
import LeaveApplicationApprovalScreen from "../SelfService/LAA/LeaveApplicationApprovalScreen";
import RoleGuard from "../../components/RoleGuard";
import { useTranslation } from "react-i18next";
import { TASKS, TASK_OPTIONS } from "../../constants/Tasks";
//#endregion

const HomePageLeaves = () => {
  //#region Const Definitions and UseRef and UseState
  const { t } = useTranslation();
  const showNewLeaveScreenRef = useRef(null);
  const showMyLeavesScreenRef = useRef(null);
  const showMyBalancesScreenRef = useRef(null);
  const showTeamScheduleScreenRef = useRef(null);
  const showTeamBalancesScreenRef = useRef(null);
  const showNewRequestForEmployeeScreenRef = useRef(null);
  const showLeaveApprovalScreenRef = useRef(null);
  const leaveToEditRef = useRef(null);
  const managerIdRef = useRef(null);
  const teamScheduleDateRef = useRef(null);
  const buttonTable = [1, 2, 3, 4, 5, 6, 7, 8];
  const [showNewLeaveScreen, setShowNewLeaveScreen] = useState(0);
  const [showMyLeavesScreen, setShowMyLeavesScreen] = useState(1);
  const [showMyBalancesScreen, setShowMyBalancesScreen] = useState(0);
  const [showTeamScheduleScreen, setShowTeamScheduleScreen] = useState(0);
  const [showTeamBalancesScreen, setShowTeamBalancesScreen] = useState(0);
  const [showNewRequestForEmployeeScreen, setShowNewRequestForEmployeeScreen] =
    useState(0);
  const [showLeaveApprovalScreen, setShowLeaveApprovalScreen] = useState(0);
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [teamScheduleDate, setTeamScheduleDate] = useState(null);

  const { colors } = useTheme();
  const styles = makeStyles(colors);
  const { account, managerEmployees } = useAccountContext();

  const resetScreens = () => {
    setShowNewLeaveScreen(0);
    setShowMyLeavesScreen(0);
    setShowMyBalancesScreen(0);
    setShowTeamScheduleScreen(0);
    setShowTeamBalancesScreen(0);
    setShowLeaveApprovalScreen(0);
    setShowNewRequestForEmployeeScreen(0);
  }

  const selectMyLeavesScreen = () => {
    resetScreens();
    setShowMyLeavesScreen(1);
  };

  const selectNewLeaveScreen = (leaveToEdit, managerId) => {
    setLeaveToEdit(leaveToEdit);
    setManagerId(managerId);
    resetScreens();
    setShowNewLeaveScreen(1);
  };

  const selectTeamsLeaveSchedulerScreen = (date) => {
    setTeamScheduleDate(date);
    resetScreens();
    setShowTeamScheduleScreen(1);
  };

  const selectLeaveApplicationApprovalScreen = () => {
    resetScreens();
    setShowLeaveApprovalScreen(1);
  };
  //#endregion

  //#region UseEffect
  useEffect(() => {
    showNewLeaveScreenRef.current = showNewLeaveScreen;
  }, [showNewLeaveScreen]);

  useEffect(() => {
    showMyLeavesScreenRef.current = showMyLeavesScreen;
  }, [showMyLeavesScreen]);

  useEffect(() => {
    showMyBalancesScreenRef.current = showMyBalancesScreen;
  }, [showMyBalancesScreen]);

  useEffect(() => {
    showTeamScheduleScreenRef.current = showTeamScheduleScreen;
  }, [showTeamScheduleScreen]);

  useEffect(() => {
    showTeamBalancesScreenRef.current = showTeamBalancesScreen;
  }, [showTeamBalancesScreen]);

  useEffect(() => {
    showNewRequestForEmployeeScreenRef.current =
      showNewRequestForEmployeeScreen;
  }, [showNewRequestForEmployeeScreen]);

  useEffect(() => {
    showLeaveApprovalScreenRef.current = showLeaveApprovalScreen;
  }, [showLeaveApprovalScreen]);

  useEffect(() => {
    leaveToEditRef.current = leaveToEdit;
  }, [leaveToEdit]);

  useEffect(() => {
    managerIdRef.current = managerId;
  }, [managerId]);

  useEffect(() => {
    teamScheduleDateRef.current = teamScheduleDate;
  }, [teamScheduleDate]);

  //* handles refresh in web
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      try {
        localStorage.setItem(
          "showNewLeaveScreen",
          showNewLeaveScreenRef.current
        );
        localStorage.setItem(
          "showMyLeavesScreen",
          showMyLeavesScreenRef.current
        );
        localStorage.setItem(
          "showMyBalancesScreen",
          showMyBalancesScreenRef.current
        );
        localStorage.setItem(
          "showTeamScheduleScreen",
          showTeamScheduleScreenRef.current
        );
        localStorage.setItem(
          "showTeamBalancesScreen",
          showTeamBalancesScreenRef.current
        );
        localStorage.setItem(
          "showNewRequestForEmployeeScreen",
          showNewRequestForEmployeeScreenRef.current
        );
        localStorage.setItem(
          "showLeaveApprovalScreen",
          showLeaveApprovalScreenRef.current
        );
        localStorage.setItem(
          "leaveToEdit",
          JSON.stringify(leaveToEditRef.current)
        );
        localStorage.setItem(
          "managerId",
          JSON.stringify(managerIdRef.current)
        );
        localStorage.setItem(
          "teamScheduleDate",
          JSON.stringify(teamScheduleDateRef.current)
        );
        localStorage.setItem("isRefresh", "true");
        localStorage.setItem("lastVisitedScreen", "HomePageLeaves");
      } catch (error) {
        console.log(error.message);
      }
    };
    if (Platform.OS === "web") {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }

  }, []);

  //* if refreshed, get data from session storage,
  //* to show same page as before refresh
  const checkIfRefreshed = async () => {
    try {
      let isRefresh = localStorage.getItem("isRefresh");
      if (isRefresh === "true") {
        localStorage.setItem("isRefresh", "false");
        setShowNewLeaveScreen(
          parseInt(localStorage.getItem("showNewLeaveScreen"))
        );
        setShowMyLeavesScreen(
          parseInt(localStorage.getItem("showMyLeavesScreen"))
        );
        setShowMyBalancesScreen(
          parseInt(localStorage.getItem("showMyBalancesScreen"))
        );
        setShowTeamScheduleScreen(
          parseInt(localStorage.getItem("showTeamScheduleScreen"))
        );
        setShowTeamBalancesScreen(
          parseInt(localStorage.getItem("showTeamBalancesScreen"))
        );
        setShowNewRequestForEmployeeScreen(
          parseInt(localStorage.getItem("showNewRequestForEmployeeScreen"))
        );
        setShowLeaveApprovalScreen(
          parseInt(localStorage.getItem("showLeaveApprovalScreen"))
        );
        setLeaveToEdit(JSON.parse(localStorage.getItem("leaveToEdit")));
        setManagerId(JSON.parse(localStorage.getItem("managerId")));
        setTeamScheduleDate(
          JSON.parse(localStorage.getItem("teamScheduleDate"))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (Platform.OS === "web") {
      checkIfRefreshed();
    }
  }, []);

  //#endregion

  return (
    <SafeAreaView style={styles.main}>
      {/* SubMenu */}
      <View>
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={true}
          directionalLockEnabled={true}
          alwaysBounceVertical={false}
          data={buttonTable}
          contentContainerStyle={{
            alignSelf: "flex-start",
            marginBottom: "1%",
            marginLeft: "2%",
          }}
          showsVerticalScrollIndicator={true}
          renderItem={({ index }) => {
            return (
              <SafeAreaView>
                {/* New Leave */}
                {index == 3 && (
                  <RoleGuard parentTask={TASKS.ALLEMPLOYEETASKS} task={TASK_OPTIONS.LEAVEAPPLICATIONS} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        selectNewLeaveScreen();
                      }}
                      text={t("navigate:NL")}
                      iconName="log-out-outline"
                      show={
                        showNewLeaveScreen && !showNewRequestForEmployeeScreen
                          ? "HOME_SELECTED"
                          : "HOME"
                      }
                      type={
                        showNewLeaveScreen && !showNewRequestForEmployeeScreen
                          ? "HOME_SELECTED"
                          : "HOME"
                      }
                      textTop="0"
                      sz={Platform.OS !== "web" ? 10 : 25}
                      isGradient={showNewLeaveScreen && !showNewRequestForEmployeeScreen ? true : false}
                    />
                  </RoleGuard>
                )}
                {/* My Leave */}
                {index == 1 && (
                  <RoleGuard parentTask={TASKS.ALLEMPLOYEETASKS} task={TASK_OPTIONS.LEAVEAPPLICATIONS} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        selectMyLeavesScreen();
                      }}
                      text={t("navigate:ML")}
                      iconName="logo-buffer"
                      show={showMyLeavesScreen ? "HOME_SELECTED" : "HOME"}
                      type={showMyLeavesScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 10 : 25}
                      isGradient={showMyLeavesScreen ? true : false}
                    />
                  </RoleGuard>
                )}
                {/* Leave Balances */}
                {index == 2 && (
                  <RoleGuard parentTask={TASKS.ALLEMPLOYEETASKS} task={TASK_OPTIONS.LEAVEAPPLICATIONS} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        resetScreens()
                        setShowMyBalancesScreen(1);
                      }}
                      text={t("navigate:BL")}
                      iconName="bar-chart-outline"
                      show={showMyBalancesScreen ? "HOME_SELECTED" : "HOME"}
                      type={showMyBalancesScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 10 : 25}
                      isGradient={showMyBalancesScreen ? true : false}
                    />
                  </RoleGuard>
                )}
                {/* Team Leave Scheduler  */}
                {index == 4 &&
                  (
                    <RoleGuard parentTask={[TASKS.UTILS, TASKS.UTILSADMIN]} task={TASK_OPTIONS.SCHEDULER} requireCRUD>
                      <CustomButton
                        onPress={() => {
                          selectTeamsLeaveSchedulerScreen();
                        }}
                        text={t("navigate:TSC")}
                        iconName="today-outline"
                        show={showTeamScheduleScreen ? "HOME_SELECTED" : "HOME"}
                        type={showTeamScheduleScreen ? "HOME_SELECTED" : "HOME"}
                        textTop="0"
                        isGradient={showTeamScheduleScreen ? true : false}
                        sz={Platform.OS !== "web" ? 10 : 25}
                      />
                    </RoleGuard>
                  )}
                {/* Team Leave balances  */}
                {index == 6 ? (
                  <RoleGuard parentTask={[TASKS.UTILS, TASKS.UTILSADMIN]} task={TASK_OPTIONS.SCHEDULER} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        resetScreens()
                        setShowTeamBalancesScreen(1);
                      }}
                      text={t("navigate:TB")}
                      iconName="people-outline"
                      show={showTeamBalancesScreen ? "HOME_SELECTED" : "HOME"}
                      type={showTeamBalancesScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 10 : 25}
                      isGradient={showTeamBalancesScreen ? true : false}

                    />
                  </RoleGuard>
                ) : (
                  <></>
                )}
                {/* Leave Application Approval  */}
                {index == 5 ? (
                  <RoleGuard parentTask={[TASKS.UTILS, TASKS.UTILSADMIN]} task={TASK_OPTIONS.LEAVEAPPROVAL} requireCRUD>
                    <CustomButton
                      onPress={() => {
                        resetScreens()
                        setShowLeaveApprovalScreen(1);
                      }}
                      text={t("navigate:LAA")}
                      iconName="people-circle-outline"
                      show={showLeaveApprovalScreen ? "HOME_SELECTED" : "HOME"}
                      type={showLeaveApprovalScreen ? "HOME_SELECTED" : "HOME"}
                      textTop="0"
                      sz={Platform.OS !== "web" ? 10 : 25}
                      isGradient={showLeaveApprovalScreen ? true : false}
                    />
                  </RoleGuard>
                ) : (
                  <></>
                )}
                {/* On Behalf of  */}
                {(managerEmployees?.length > 0 &&
                  index == 7) && (
                    <RoleGuard parentTask={TASKS.DELIGATE} task={TASK_OPTIONS.LEAVEAPPLICATIONS} requireCRUD>
                      <CustomButton
                        onPress={() => {
                          selectNewLeaveScreen(null, account.employeeId);
                          setShowNewRequestForEmployeeScreen(1);
                        }}
                        text={t("navigate:NLE")}
                        iconName="person-add-outline"
                        show={
                          showNewRequestForEmployeeScreen
                            ? "HOME_SELECTED"
                            : "HOME"
                        }
                        type={
                          showNewRequestForEmployeeScreen
                            ? "HOME_SELECTED"
                            : "HOME"
                        }
                        textTop="0"
                        sz={Platform.OS !== "web" ? 10 : 25}
                        isGradient={showNewRequestForEmployeeScreen ? true : false}
                      />
                    </RoleGuard>
                  )}
              </SafeAreaView>
            );
          }}
          keyExtractor={(item) => `${item}`}
        />
      </View>
      {/* Views to show depending on Flatlist Selection */}
      <SafeAreaView style={[styles.mainSubStyle]}>
        {showNewLeaveScreen ? (
          <NewLeaveScreen
            leaveDetails={leaveToEdit}
            managerId={managerId}
            selectMyLeavesScreen={selectMyLeavesScreen}
          />
        ) : (
          <></>
        )}

        {showMyLeavesScreen ? (
          <MyLeaves selectNewLeaveScreen={selectNewLeaveScreen} />
        ) : (
          <></>
        )}
        {showMyBalancesScreen ? (
          <PersonalLeaveBalancesScreen
            AnnualLeaveOnly={false}
            ShowDetails={true}
          />
        ) : (
          <></>
        )}
        {showTeamScheduleScreen ? (
          <TeamsLeaveSchedulerScreen
            LAA={false}
            date={teamScheduleDate}
            setSelectedStartDate={setTeamScheduleDate}
            selectLeaveApplicationApprovalScreen={
              selectLeaveApplicationApprovalScreen
            }
          />
        ) : (
          <></>
        )}
        {showTeamBalancesScreen ? <TeamsLeaveBalancesScreen /> : <></>}
        {showLeaveApprovalScreen ? (
          <LeaveApplicationApprovalScreen
            selectTeamsLeaveSchedulerScreen={selectTeamsLeaveSchedulerScreen}
          />
        ) : (
          <></>
        )}
      </SafeAreaView>
    </SafeAreaView>
  );
};

const makeStyles = (colors, width) =>
  StyleSheet.create({
    main: {
      flex: 1,
      marginHorizontal: 10,
      marginTop: "1%",
    },
    mainSubStyle: {
      flex: 1,
      backgroundColor: colors.background,
    },
    myLeaves: {
      flex: 1,
      backgroundColor: "transparent",
    },
    rowHome: {
      flexDirection: "row",
      height: "100%",
    },
    circlesRowContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-evenly",
    },
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-evenly",
    },
    circle: {
      width: 70,
      height: 70,
      borderRadius: 35,
      borderWidth: 4,
      borderColor: colors.buttons_primary,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      marginTop: 50,
      marginBottom: 10,
    },
    textBelowCircle: {
      fontSize: 14,
      textAlign: "center",
      color: colors.text_primary,
      fontWeight: "bold",
    },
    circleText: {
      color: colors.text_primary,
      fontSize: 14,
      fontWeight: "bold",
    },
    circleCenter: {
      alignItems: "center",
    },
  });

export default HomePageLeaves;
